import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"

/**
 * NewBadge
 * - Displays a "New" badge.
 * - Blue (strong) as long as it hasn't been "seen"; becomes soft after.
 * - Remembers the state via localStorage (configurable key) for easy reuse elsewhere.
 *
 * Props:
 * - storageKey (string, required): localStorage key to remember the seen state.
 * - label (string, optional): badge text, default "New".
 * - active (bool, optional): if false, displays nothing. Default true.
 * - currentPath (string, optional): current path (e.g., from react-router), to mark as seen automatically.
 * - seenOnPathPrefix (string|string[]|undefined): if provided, when currentPath starts with this/these prefix(es), mark as seen.
 * - className (string, optional): additional classes.
 * - ariaLabel (string, optional): custom aria-label; otherwise label.
 */
const NewBadge = ({
  storageKey,
  label = "New",
  active = true,
  currentPath,
  seenOnPathPrefix,
  className = "",
  ariaLabel,
}) => {
  const { t } = useTranslation()
  const prefixes = useMemo(() => {
    if (!seenOnPathPrefix) return []
    return Array.isArray(seenOnPathPrefix)
      ? seenOnPathPrefix
      : [seenOnPathPrefix]
  }, [seenOnPathPrefix])

  const [hasSeen, setHasSeen] = useState(() => {
    try {
      if (
        typeof globalThis !== "undefined" &&
        globalThis.window?.localStorage
      ) {
        return globalThis.window.localStorage.getItem(storageKey) === "true"
      }
    } catch (e) {
      console.log("Error reading NewBadge seen state:", e)
      void 0
    }
    return false
  })

  // Mark as seen if the current URL matches one of the prefixes
  useEffect(() => {
    if (!prefixes.length || !currentPath) return
    const matches = prefixes.some((p) => currentPath.startsWith(p))
    if (matches && !hasSeen) {
      try {
        globalThis?.localStorage?.setItem(storageKey, "true")
      } catch (e) {
        console.log("newBadge : error saving seen state:", e)
        void 0
      }
      setHasSeen(true)
    }
  }, [currentPath, prefixes, hasSeen, storageKey])

  if (!active) return null

  const classes = ["NavNewBadge", hasSeen ? "" : "NavNewBadge--loud", className]
    .filter(Boolean)
    .join(" ")

  return (
    <span className={classes} aria-label={ariaLabel || label}>
      {t("common:navigationBar.newBadge") || label}
    </span>
  )
}

NewBadge.propTypes = {
  storageKey: PropTypes.string.isRequired,
  label: PropTypes.string,
  active: PropTypes.bool,
  currentPath: PropTypes.string,
  seenOnPathPrefix: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
}

export default NewBadge
